import type { LoaderFunctionArgs } from "react-router";
import { returnsAuthorization } from "src/configs/authorization/returns";
import { PAGES, PAGE_URLS } from "src/configs/pages-urls";
import { authorizationLoader } from "src/router/common-loaders";
import { convertFrameworkRoute } from "src/utils/routes";

const returnReasonsV2Routes = [
	{
		index: true,
		lazy: () =>
			import("src/module--returns/pages/ReturnsReasons.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: `${PAGES.edit}/:returnReasonsId`,
		lazy: () =>
			import("src/module--returns/pages/ReturnsReasonsEdit.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: PAGES.new,
		lazy: () =>
			import("src/module--returns/pages/ReturnsReasonsNew.page").then(
				convertFrameworkRoute,
			),
	},
];

export default [
	{
		path: "",
		lazy: () =>
			import("src/module--returns/pages/ReturnsDashboard.page").then(
				convertFrameworkRoute,
			),
		loader: async ({
			params: { accountId: accountIdParam },
		}: LoaderFunctionArgs) =>
			authorizationLoader(
				{
					accountIdParam,
					conditionalRedirect: true,
				},
				returnsAuthorization.returns.configuration,
				[
					{
						redirectUrl: PAGE_URLS.returns().shopifyReturns,
						abilityArguments:
							returnsAuthorization.returns.shopifyReturnsReports,
					},
					{
						redirectUrl: PAGE_URLS.returns().networkMonitoring,
						abilityArguments: returnsAuthorization.returns.insights,
					},
				],
			),
	},
	{
		path: PAGES.returns.sub.approvals,
		lazy: () =>
			import("src/module--returns/pages/ReturnsApprovals.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: PAGES.returns.sub.publish,
		lazy: () =>
			import("src/module--returns/pages/ReturnsPublish.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: PAGES.returns.sub.portals,
		lazy: () =>
			import("src/module--returns/pages/ReturnsPortals.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: `${PAGES.returns.sub.portals}/${PAGES.edit}/:portalId`,
		lazy: () =>
			import("src/module--returns/pages/ReturnsPortalEdit.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: `${PAGES.returns.sub.portals}/${PAGES.new}`,
		lazy: () =>
			import("src/module--returns/pages/ReturnsPortalNew.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: PAGES.returns.sub.translations,
		lazy: () =>
			import("src/module--returns/pages/ReturnsTranslations.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: `${PAGES.returns.sub.translations}/${PAGES.edit}/:translationId`,
		lazy: () =>
			import(
				"src/module--returns/pages/ReturnsTranslationEdit.page"
			).then(convertFrameworkRoute),
	},
	{
		path: PAGES.returns.sub.orderApis,
		lazy: () =>
			import("src/module--returns/pages/ReturnsOrderApis.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: `${PAGES.returns.sub.orderApis}/${PAGES.edit}/:orderApiId`,
		lazy: () =>
			import("src/module--returns/pages/ReturnsOrderApiEdit.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: `${PAGES.returns.sub.orderApis}/${PAGES.new}`,
		lazy: () =>
			import("src/module--returns/pages/ReturnsOrderApiNew.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: PAGES.returns.sub.courierApis,
		lazy: () =>
			import("src/module--returns/pages/ReturnsCourierApis.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: `${PAGES.returns.sub.courierApis}/${PAGES.edit}/:courierApiId`,
		lazy: () =>
			import("src/module--returns/pages/ReturnsCourierApiEdit.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: `${PAGES.returns.sub.courierApis}/${PAGES.new}`,
		lazy: () =>
			import("src/module--returns/pages/ReturnsCourierApiNew.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: PAGES.returns.sub.returnReasons,
		lazy: () =>
			import("src/module--returns/pages/ReturnsRules.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: PAGES.returns.sub.returnReasonsV2,
		children: returnReasonsV2Routes,
		loader: async ({
			params: { accountId: accountIdParam },
		}: LoaderFunctionArgs) =>
			authorizationLoader(
				{ accountIdParam },
				returnsAuthorization.returns.returnReasonsV2,
			),
	},
	{
		path: `${PAGES.returns.sub.returnReasons}/${PAGES.edit}/:ruleId`,
		lazy: () =>
			import("src/module--returns/pages/ReturnsRulesEdit.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: `${PAGES.returns.sub.returnReasons}/${PAGES.new}`,
		lazy: () =>
			import("src/module--returns/pages/ReturnsRulesNew.page").then(
				convertFrameworkRoute,
			),
	},
	{
		path: PAGES.returns.sub.returnsCompensationMethods,
		lazy: () =>
			import(
				"src/module--returns/pages/ReturnsCompensationMethods.page"
			).then(convertFrameworkRoute),
	},
	{
		path: `${PAGES.returns.sub.returnsCompensationMethods}/edit/:portalId`,
		lazy: () =>
			import(
				"src/module--returns/pages/ReturnsCompensationMethodEdit.page"
			).then(convertFrameworkRoute),
	},
	{
		path: PAGES.returns.sub.returnEligibility,
		lazy: () =>
			import(
				"src/module--returns/pages/ReturnsReturnEligibilities.page"
			).then(convertFrameworkRoute),
	},
	{
		path: `${PAGES.returns.sub.returnEligibility}/${PAGES.edit}/:portalId`,
		lazy: () =>
			import(
				"src/module--returns/pages/ReturnsReturnEligibilityEdit.page"
			).then(convertFrameworkRoute),
	},
	{
		path: PAGES.returns.sub.articleEligibility,
		lazy: () =>
			import(
				"src/module--returns/pages/ReturnsArticlesEligibility.page"
			).then(convertFrameworkRoute),
	},
	{
		path: `${PAGES.returns.sub.articleEligibility}/${PAGES.edit}/:portalId`,
		lazy: () =>
			import(
				"src/module--returns/pages/ReturnsArticleEligibilityEdit.page"
			).then(convertFrameworkRoute),
	},
	{
		path: PAGES.returns.sub.carrierAvailability,
		lazy: () =>
			import(
				"src/module--returns/pages/ReturnsCarriersAvailability.page"
			).then(convertFrameworkRoute),
	},
	{
		path: `${PAGES.returns.sub.carrierAvailability}/${PAGES.edit}/:portalId`,
		lazy: () =>
			import(
				"src/module--returns/pages/ReturnsCarrierAvailabilityEdit.page"
			).then(convertFrameworkRoute),
	},
	{
		path: PAGES.returns.sub.shopifyReturns,
		lazy: () =>
			import("src/components/Report").then((m) => ({
				Component: m.ShopifyReturnsReport,
			})),
	},
	{
		path: PAGES.returns.sub.shopifyProductReturns,
		lazy: () =>
			import("src/components/Report").then((m) => ({
				Component: m.ShopifyProductReturnsReport,
			})),
	},
	{
		path: PAGES.returns.sub.networkMonitoring,
		lazy: () =>
			import("src/components/Report").then((m) => ({
				Component: m.ReturnsNetworkMonitoringReport,
			})),
	},
	{
		path: PAGES.returns.sub.registrations,
		lazy: () =>
			import("src/components/Report").then((m) => ({
				Component: m.ReturnsRegistrationsReport,
			})),
	},
	{
		path: PAGES.returns.sub.itemInsights,
		lazy: () =>
			import("src/components/Report").then((m) => ({
				Component: m.ReturnsItemInsightsReport,
			})),
	},
	{
		path: PAGES.returns.sub.forecastPromotion,
		lazy: () =>
			import(
				"src/module--returns/pages/ReturnsForecastPromotion.page"
			).then(convertFrameworkRoute),
	},
	{
		path: PAGES.returns.sub.forecast,
		lazy: () =>
			import(
				"src/module--returns/pages/returns-forecast-report.page"
			).then(convertFrameworkRoute),
	},
	{
		path: `${PAGES.returns.sub.approvals}/edit/:id`,
		lazy: () =>
			import("src/module--returns/pages/ReturnsApprovalDetail.page").then(
				convertFrameworkRoute,
			),
	},
];
